import type { DataExportTestScheduleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response ({ message }) to the GraphQL mutation
 * response shape so consumers branching on the result do not need to know which
 * path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  message: string;
}): DataExportTestScheduleMutation => ({
  dataExportTestSchedule: {
    __typename: 'DataExportTestScheduleOutput',
    message: data.message,
  },
});

/**
 * NOTE: the test run is DISPATCH-ONLY on both paths — the mutation resolves once
 * the export has been kicked off, and the outcome shows up later as a row in the
 * execution history. Only `getScheduleExecutions` is invalidated: a test run
 * never changes the active schedule.
 */
export const useTestDataExportScheduleTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.dataExport.testSchedule.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataExport.getScheduleExecutions.queryKey(),
        });
      },
    }),
  });

  return {
    testDataExportSchedule: async (
      scheduleId: string
    ): Promise<DataExportTestScheduleMutation> => {
      const result = await mutation.mutateAsync({ id: scheduleId });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
